import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PHASES_OF_PLAY, PHASE_OF_PLAY_META } from '@4cm/core/phaseOfPlay';
import type { PhaseCoverage, PhaseOfPlay } from '@4cm/core/phaseOfPlay';
import { Pitch } from '../components/Pitch';
import { Mic } from '../components/primitives';
import { supabase } from '../lib/supabase';
import { loadLocalSessions } from '../lib/localTeam';
import { useSessionDrills } from '../lib/queries/useSessionDrills';
import { layoutForDrill } from '../lib/pitchLayouts';
import './SessionRun.css';

interface SessionRow {
  id: string;
  date: string;
  title: string | null;
  duration_mins: number | null;
  objectives: string[] | null;
  psych_social_intent: string | null;
  phase_coverage: Partial<PhaseCoverage> | null;
}

function topPhase(coverage: Partial<PhaseCoverage> | null | undefined): PhaseOfPlay {
  let best: PhaseOfPlay = 'attacking';
  let bestValue = 0;
  for (const phase of PHASES_OF_PLAY) {
    const value = Number(coverage?.[phase] ?? 0);
    if (value > bestValue) {
      best = phase;
      bestValue = value;
    }
  }
  return best;
}

// Trim a description into a single coaching cue — first sentence, capped, no trailing dot.
function firstSentence(text: string | null | undefined): string | null {
  if (!text) return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  const dot = trimmed.search(/[.!?]\s|[.!?]$/);
  const head = dot > 0 ? trimmed.slice(0, dot) : trimmed;
  return head.length > 96 ? `${head.slice(0, 93).trimEnd()}…` : head;
}

export function SessionRun() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState<SessionRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drillIndex, setDrillIndex] = useState(0);
  const { drills } = useSessionDrills(id);

  const currentDrill = drills[drillIndex]?.drill ?? drills.find((row) => row.drill)?.drill ?? null;
  const totalDrills = drills.length;
  const phase = currentDrill?.primary_phase ?? topPhase(session?.phase_coverage);
  const phaseMeta = PHASE_OF_PLAY_META[phase];
  const pitchLayout = useMemo(
    () =>
      currentDrill
        ? layoutForDrill({
            name: currentDrill.name,
            primary_phase: currentDrill.primary_phase,
            sub_themes: currentDrill.sub_themes,
          })
        : null,
    [currentDrill],
  );

  // Coaching point: per-drill description's first sentence → fallback to session intent → static cue.
  const drillCue = firstSentence(currentDrill?.description);
  const intentCue = firstSentence(session?.psych_social_intent);
  const coachingPoint =
    drillCue
    ?? intentCue
    ?? 'Keep the cue short, run the picture, then stop only when the next rep needs a cleaner trigger.';
  // When cp falls back to the session intent, suppress the duplicate intent paragraph below.
  const showIntentLine = !!session?.psych_social_intent && drillCue !== null;

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      let dbError: string | null = null;
      try {
        const { data, error: loadError } = await supabase
          .from('sessions')
          .select('id, date, title, duration_mins, objectives, psych_social_intent, phase_coverage')
          .eq('id', id)
          .maybeSingle();
        if (cancelled) return;
        if (loadError) dbError = loadError.message;
        else if (data) {
          setSession(data as SessionRow);
          setError(null);
          return;
        }
      } catch (err) {
        dbError = err instanceof Error ? err.message : String(err);
      }
      if (cancelled) return;
      const local = loadLocalSessions().find((s) => s.id === id);
      if (local) {
        setSession({
          id: local.id,
          date: local.date,
          title: local.title,
          duration_mins: local.duration_mins,
          objectives: local.objectives,
          psych_social_intent: local.psych_social_intent,
          phase_coverage: local.phase_coverage,
        });
        setError(null);
        return;
      }
      setSession(null);
      setError(dbError ?? 'Session not found.');
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const isLastDrill = totalDrills === 0 || drillIndex >= totalDrills - 1;
  const nextLabel = isLastDrill ? 'Finish' : 'Next';
  // Per AGENTS.md "no disabled buttons" — Next at the last drill (or with no drills)
  // navigates back to the session detail / review for follow-up notes.
  const handleNext = () => {
    if (!isLastDrill) {
      setDrillIndex((i) => i + 1);
      return;
    }
    if (id) navigate(`/sessions/${id}`);
    else navigate('/');
  };

  const duration = session?.duration_mins ?? 25;

  return (
    <main className="session-run" data-phase={phase}>
      <header className="session-run__topbar">
        <Link to="/" className="session-run__link">
          Today
        </Link>
        {id && (
          <Link to={`/sessions/${id}`} className="session-run__link">
            Detail + review
          </Link>
        )}
      </header>

      <div className="session-run__status" role="status">
        {totalDrills > 0 && (
          <>
            <span className="session-run__counter" aria-label={`Drill ${drillIndex + 1} of ${totalDrills}`}>
              {drillIndex + 1} / {totalDrills}
            </span>
            <span className="session-run__sep" aria-hidden="true">·</span>
          </>
        )}
        <span className="session-run__drill-name">
          {currentDrill?.name ?? phaseMeta.label}
        </span>
        <span className="session-run__sep" aria-hidden="true">·</span>
        <span className="session-run__time">
          <span>{duration}</span>
          <small> min left</small>
        </span>
      </div>

      <section className="session-run__hero" aria-label="Run session">
        <div className="session-run__eyebrow">
          <span aria-hidden="true">{phaseMeta.glyph}</span>
          {phaseMeta.label}
        </div>
        <h1>{session?.title ?? 'Run session'}</h1>
        <p className="session-run__cp">
          <span className="session-run__cp-glyph" aria-hidden="true">cp ·</span>
          {coachingPoint}
        </p>
        {showIntentLine && (
          <p className="session-run__intent">{session?.psych_social_intent}</p>
        )}
      </section>

      {error && <p className="session-run__error">{error}</p>}

      <section className="session-run__pitch" aria-label="Pitch picture">
        <Pitch
          caption={currentDrill?.name?.toUpperCase() ?? phaseMeta.label.toUpperCase()}
          width={920}
          height={480}
          positions={pitchLayout ?? undefined}
        />
      </section>

      <footer className="session-run__actions" aria-label="Run controls">
        <Mic size="touchline" aria-label="Hold to talk to Brain" />
        <button
          type="button"
          className="session-run__next"
          onClick={handleNext}
          aria-label={
            isLastDrill
              ? 'Finish session and open review'
              : `Advance to drill ${drillIndex + 2} of ${totalDrills}`
          }
        >
          {nextLabel}
        </button>
      </footer>
    </main>
  );
}
